<script setup>
import {Icon} from "@iconify/vue";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import AllButtons from "@/views/orders/Shared/AllButtons.vue";
import {useRouter, useRoute} from "vue-router";
import {onMounted, ref, computed} from "vue";
import {useMainOrdersStore} from '@/stores/mainOrders.js'
import {useConfigStore} from '@/stores/config.js'
import {formatPrice} from "@/utils/priceFormatter.js";
import {gregorianDate} from "@/utils/gregorianDate.js";

/* ---------- vue-router ---------- */
const router = useRouter();
const route = useRoute();

/* ---------- Stores ---------- */
const ordersStore = useMainOrdersStore()
const configStore = useConfigStore()

const searchTerm = ref('')
const games = computed(() => configStore.getGamesByService('StocksManager') || []);
const gameObject = computed(() => games.value.find(g => g.abbreviation === route.params.game) || {})
const servers = computed(() => ordersStore.stocks.filter(s => s.game === gameObject.value.game))
const accountTitleMap = computed(() => Object.fromEntries(
    ordersStore.gameAccounts.map(acc => [acc.id, acc.title])
))

// حالا برای گرفتن title:
const getAccountTitle = computed(() => (accountId) =>
    accountTitleMap.value[accountId] || 'Disabled Account'
)
const rowsWithAccountTitle = computed(() => {
  return servers.value.map(row => ({
    ...row,
    accountTitle: getAccountTitle.value(row.accountID),
  }));
});

onMounted(() => {
  ordersStore.action_getOverviewStocks();
  ordersStore.action_getGameAccounts()
})


const columns = ref([
  {label: 'Server', field: 'server', thClass: 'custom-head-cell', tdClass: 'custom-cell cursor-pointer'},
  {
    label: 'Account',
    field: 'accountTitle',
    thClass: 'custom-head-cell',
    tdClass: 'overflow-hidden whitespace-nowrap text-ellipsis max-w-[100px] cursor-pointer custom-cell'
  },
  {
    label: 'Amount',
    field: 'amount',
    thClass: 'custom-head-cell',
    tdClass: 'overflow-hidden whitespace-nowrap text-ellipsis max-w-[100px] custom-cell'
  },
  {label: 'Price', field: 'price', thClass: 'custom-head-cell', tdClass: 'custom-cell'},
  {
    label: 'Last Update',
    field: 'lastUpdate',
    thClass: 'max-w-max custom-head-cell',
    tdClass: 'max-w-max cursor-pointer custom-cell'
  }
])

const search = computed(() => ordersStore.boostOrdersSearch);


function orderDetails(details) {
  router.push({name: 'stocks-new-transfer', params: {server: details.row.server}})
}


</script>

<template>
  <div>
    <div class="flex flex-col gap-4">
      <div class="flex flex-row justify-between gap-4">
        <h1 class="text-white text-base">{{ gameObject.game }}</h1>
        <AllButtons :show-filter="false" :showCategoriesButtons="false"
                    v-model="searchTerm"/>
      </div>
      <div v-if="ordersStore.isLoading" class="flex items-center justify-center h-60">
        <Icon icon="eos-icons:bubble-loading" class="size-12"/>
      </div>
      <vue-good-table v-else
                      :fixed-header="false"
                      max-height="calc(var(--vh, 1vh) * 100 - 12rem)"
                      :columns="columns"
                      compactMode
                      class="custom-scrollbar-2"
                      styleClass="vgt-table centered lesspadding2 striped ltr text-left custom-scrollbar-2"
                      row-style-class="!text-left normal-case !bg-none hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31]"
                      :rows="rowsWithAccountTitle"
                      :search-options="{
                        enabled: true,
                        externalQuery: searchTerm,
                      }"
                      v-on:row-click="orderDetails">
        <template v-slot:table-row="props">
          <div v-if="props.column.field === 'amount'" class="block w-full whitespace-nowrap" @click.stop>
            {{ props.row.amount }}
          </div>
          <div v-if="props.column.field === 'price'" class="block w-full whitespace-nowrap" @click.stop>
            {{ props.row.price ? formatPrice(props.row.price) + ' $' : '' }}
          </div>
          <span v-if="props.column.field === 'lastUpdate'" class="block w-full max-w-max">
            {{ gregorianDate(props.row.lastUpdate, 'DD MMM HH:mm') }}
          </span>
        </template>
      </vue-good-table>
    </div>
  </div>
</template>

<style lang="scss">
.custom-head-cell {
  background-image: none !important;
  background-color: #1A2941 !important;
  font-size: .75rem !important;
  white-space: nowrap !important;
  padding: .5rem .5rem .5rem 1rem !important;
  text-transform: capitalize !important;
  text-align: left !important;
}

.custom-cell {
  text-align: left !important;
  padding: .3rem !important;
  font-size: .75rem !important;
}

/**
  Applying `border-collapse: separate` on the table container prevents the first
  table header row's border from being applied elsewhere when it is sticky.
*/
.vgt-table {
  border-collapse: separate !important;
  border-spacing: 0;
  border-top: 0 !important;
}

/**
    Applying `position: sticky` here to make it sticky.
*/
.vgt-table thead {
  position: sticky;
  top: 0
}

/**
    We removed the top border from .vgt-table to apply it here instead
*/
.vgt-table thead tr:first-child th {
  border-top: 1px solid rgb(51, 65, 85) !important;
}

/**
    Applying `border-collapse: separate` doubled the cell border width visually.
    Setting this to a fractional value fixed the issue.
*/
/*.vgt-table th, .vgt-table td {
  border-width: .25px !important
}*/

table.vgt-table td {
  text-transform: none;
}
</style>